import { useEffect } from "react";
import {
  getFloor,
  getFloors,
  getDesks,
  resetGetFloorStatus,
  selectGetFloorStatus,
  selectGetFloorsStatus,
  selectGetDesksStatus,
  resetGetDesksStatus,
  resetGetFloorsStatus,
  selectGetSensorsStatus,
  getSensors,
  resetGetSensorsStatus,
} from "../redux/slices/groups.slice";
import {
  getStats,
  resetGetStatsStatus,
  selectGetStatsStatus,
} from "../redux/slices/reports.slice";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  getDoors,
  selectGetDoorsError,
  selectGetDoorsStatus,
} from "../redux/slices/doors.slice";

type HookOptionsType = {
  noCache?: boolean;
  skip?: boolean;
};

export function useGetFloorQuery(id: string, options?: HookOptionsType) {
  const { noCache, skip } = options || {};

  const dispatch = useAppDispatch();

  const status = useAppSelector((state) => selectGetFloorStatus(state, id));

  const isUninitialized = status === undefined;
  const isLoading = status === "pending" || status === undefined;
  const isError = status === "rejected";
  const isSuccess = status === "fulfilled";

  const refetch = () => {
    if (!skip) {
      dispatch(resetGetFloorStatus({ id }));
    }
  };

  useEffect(() => {
    if (noCache && (status === "rejected" || status === "fulfilled") && !skip) {
      dispatch(resetGetFloorStatus({ id }));
    }
  }, []);

  useEffect(() => {
    if (status === undefined && !skip) {
      dispatch(getFloor({ id }));
    }
  }, [dispatch, status, id, skip]);

  return { isUninitialized, isLoading, isError, isSuccess, refetch };
}

export function useGetFloorsQuery(
  locationID: string,
  options?: HookOptionsType
) {
  const { noCache, skip } = options || {};

  const dispatch = useAppDispatch();

  const status = useAppSelector((state) =>
    selectGetFloorsStatus(state, locationID)
  );

  const isUninitialized = status === undefined;
  const isLoading = status === "pending" || status === undefined;
  const isError = status === "rejected";
  const isSuccess = status === "fulfilled";

  const refetch = () => {
    if (!skip) {
      dispatch(resetGetFloorsStatus({ locationID }));
    }
  };

  useEffect(() => {
    if (noCache && (status === "rejected" || status === "fulfilled") && !skip) {
      dispatch(resetGetFloorsStatus({ locationID }));
    }
  }, []);

  useEffect(() => {
    if (status === undefined && !skip) {
      dispatch(getFloors({ locationID }));
    }
  }, [dispatch, status, locationID, skip]);

  return { isUninitialized, isLoading, isError, isSuccess, refetch };
}

export function useGetDesksQuery(floorID: string, options?: HookOptionsType) {
  const { noCache, skip } = options || {};

  const dispatch = useAppDispatch();

  const status = useAppSelector((state) =>
    selectGetDesksStatus(state, floorID)
  );

  const isUninitialized = status === undefined;
  const isLoading = status === "pending" || status === undefined;
  const isError = status === "rejected";
  const isSuccess = status === "fulfilled";

  const refetch = () => {
    if (!skip) {
      dispatch(resetGetDesksStatus({ floorID }));
    }
  };

  useEffect(() => {
    if (noCache && (status === "rejected" || status === "fulfilled") && !skip) {
      dispatch(resetGetDesksStatus({ floorID }));
    }
  }, []);

  useEffect(() => {
    if (status === undefined && !skip) {
      dispatch(getDesks({ floorID }));
    }
  }, [dispatch, status, floorID, skip]);

  return { isUninitialized, isLoading, isError, isSuccess, refetch };
}

export function useGetSensorsQuery(floorID: string, options?: HookOptionsType) {
  const { noCache, skip } = options || {};

  const dispatch = useAppDispatch();

  const status = useAppSelector((state) =>
    selectGetSensorsStatus(state, floorID)
  );

  const isUninitialized = status === undefined;
  const isLoading = status === "pending" || status === undefined;
  const isError = status === "rejected";
  const isSuccess = status === "fulfilled";

  const refetch = () => {
    if (!skip) {
      dispatch(resetGetSensorsStatus({ floorID }));
    }
  };

  useEffect(() => {
    if (noCache && (status === "rejected" || status === "fulfilled") && !skip) {
      dispatch(resetGetSensorsStatus({ floorID }));
    }
  }, []);

  useEffect(() => {
    if (status === undefined && !skip) {
      dispatch(getSensors({ floorID }));
    }
  }, [dispatch, status, floorID, skip]);

  return { isUninitialized, isLoading, isError, isSuccess, refetch };
}

// StateHandler owns initial and periodic door reconciliation so floor pages do
// not race a second GET owner. This hook exposes request state and an explicit
// user retry only.
export function useDoorsReconciliationStatus(
  floorID: string,
  options?: HookOptionsType,
) {
  const { skip } = options || {};
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) =>
    selectGetDoorsStatus(state, floorID),
  );
  const error = useAppSelector((state) => selectGetDoorsError(state, floorID));

  const isUninitialized = status === undefined;
  const isLoading = status === "pending" || status === undefined;
  const isError = status === "rejected";
  const isSuccess = status === "fulfilled";

  const refetch = () => {
    if (!skip && floorID) {
      dispatch(getDoors({ floorID }));
    }
  };

  return {
    isUninitialized,
    isLoading,
    isError,
    isSuccess,
    error,
    refetch,
  };
}

export function useGetStatsQuery(
  locationID: string,
  options?: HookOptionsType
) {
  const { noCache, skip } = options || {};

  const dispatch = useAppDispatch();

  const status = useAppSelector((state) =>
    selectGetStatsStatus(state, locationID)
  );

  const isUninitialized = status === undefined;
  const isLoading = status === "pending";
  const isError = status === "rejected";
  const isSuccess = status === "fulfilled";

  const refetch = () => {
    if (!skip) {
      dispatch(resetGetStatsStatus({ locationID }));
    }
  };

  useEffect(() => {
    if (noCache && (status === "rejected" || status === "fulfilled") && !skip) {
      dispatch(resetGetStatsStatus({ locationID }));
    }
  }, []);

  useEffect(() => {
    if (status === undefined && !skip) {
      dispatch(getStats({ locationID }));
    }
  }, [dispatch, status, locationID, skip]);

  return { isUninitialized, isLoading, isError, isSuccess, refetch };
}
